import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardMedia,
  CardContent,
  useTheme,
  useMediaQuery,
  Modal
} from '@mui/material';
import { parse, format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { useSwipeable } from 'react-swipeable';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import rawTourDates from '../data/shows.json';
import PaymentModal, { PaymentTypes } from '../components/PaymentModal';

interface ShowData {
  date: string;
  doortime: string;
  showtime: string;
  location: string;
  flyer: string;
  paymentTypes?: PaymentTypes;  
}

const timeZone = 'America/New_York';

// Utility to parse date/time in your specified timezone
function parseShowDate(dateString: string, timeString: string) {
  const parsed = parse(`${dateString} ${timeString}`, 'yyyy-MM-dd HH:mm', new Date());
  return toZonedTime(parsed, timeZone);
}

const Tour: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Sort shows by date ascending
  const sortedDates: ShowData[] = useMemo(() => {
    return [...rawTourDates].sort((a, b) => {
      const aDate = parseShowDate(a.date, a.doortime);
      const bDate = parseShowDate(b.date, b.doortime);
      return aDate.valueOf() - bDate.valueOf();
    });
  }, []);

  // Find first upcoming or default to 0
  const now = new Date();
  const firstUpcomingIndex = sortedDates.findIndex(
    (show) => new Date(show.date) >= now
  );
  const initialIndex = firstUpcomingIndex === -1 ? 0 : firstUpcomingIndex;

  const [currentIndex, setCurrentIndex] = useState<number>(initialIndex);

  // Flyer modal state
  const [openFlyer, setOpenFlyer] = useState(false);
  // Payment modal state
  const [openPaymentModal, setOpenPaymentModal] = useState(false);

  // Swipe logic
  const handlers = useSwipeable({
    onSwipedLeft: () => handleNext(),
    onSwipedRight: () => handlePrev(),
  });

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % sortedDates.length);
  };
  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? sortedDates.length - 1 : prev - 1));
  };

  // Grab the current show
  const currentTour = sortedDates[currentIndex];
  const doorDateTime = parseShowDate(currentTour.date, currentTour.doortime);
  const showDateTime = parseShowDate(currentTour.date, currentTour.showtime);
  const showDate = new Date(currentTour.date);
  const isPast = showDate < now;

  // Check if paymentTypes is empty or non-existent
  const isPaymentEmpty =
    !currentTour.paymentTypes ||
    Object.values(currentTour.paymentTypes).filter(Boolean).length === 0;

  // Decide button label, disabled state, and onClick
  let buttonLabel = 'Get Tickets';
  let buttonDisabled = false;
  let buttonOnClick: React.MouseEventHandler<HTMLButtonElement> | undefined =
    undefined;

  if (isPast) {
    buttonLabel = "Show's Over";
    buttonDisabled = true;
  } else if (isPaymentEmpty) {
    buttonLabel = 'Free Show';
    buttonDisabled = false;
    // If you don't want any action, just leave onClick undefined or set a no-op:
    buttonOnClick = () => {};
  } else {
    // Show Payment Modal
    buttonLabel = 'Get Tickets';
    buttonDisabled = false;
    buttonOnClick = () => setOpenPaymentModal(true);
  }  

  // Formatting
  // Date formats
  // "March 23, Doors at: 7:00, Show: 9:00"
  const displayFull = `${format(doorDateTime, 'MMMM d')}, Doors at: ${format(doorDateTime, 'h:mm')}, Show: ${format(showDateTime, 'h:mm')}`;

  // "3/23, 7:00"
  const displayShort = `${format(doorDateTime, 'M/d')}, ${format(doorDateTime, 'h:mm')}`;
  
  // "Sunday, March 23, 7PM"
  const displayWithDay = `${format(doorDateTime, 'EEEE')}, ${format(
    doorDateTime,
    'MMMM d'
  )}, ${format(doorDateTime, 'ha')}`;

  // Flyer modal handlers
  const handleFlyerClick = () => {
    setOpenFlyer(true);
  };
  const handleCloseFlyer = () => {
    setOpenFlyer(false);
  };

  const handleClosePaymentModal = () => {
    setOpenPaymentModal(false);
  };

  return (
    <Box
      sx={{
        p: 4,
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
      {...handlers}
    >
      <Typography
        variant={isMobile ? 'h4' : 'h3'}
        sx={{ mb: 4, color: theme.palette.secondary.main }}
      >
        Tour Dates
      </Typography>

      <Card
        sx={{
          maxWidth: isMobile ? '90%' : 600,
          mx: 'auto',
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.secondary,
        }}
      >
        {/* Flyer image -> opens flyer modal */}
        <CardMedia
          component="img"
          height={isMobile ? 250 : 350}
          image={currentTour.flyer}
          alt={`Flyer for ${currentTour.location}`}
          sx={{ cursor: 'pointer' }}
          onClick={handleFlyerClick}
        />
        <CardContent sx={{ pt: 2, pb: 2 }}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            {currentTour.location}
          </Typography>
          <Typography variant="body2">{displayWithDay}</Typography>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 2,
              mt: 2,
            }}
          >
            {/* Previous Button with icon */}
            <Button
              onClick={handlePrev}
              variant="text"
              sx={{
                color: theme.palette.secondary.main,
                minWidth: 'auto',
                p: 1,
              }}
            >
              <ArrowBackIosIcon fontSize="large" />
            </Button>

            {/* "Get Tickets" -> Payment Modal */}
            <Button
              variant="contained"
              color="secondary"
              disabled={buttonDisabled}
              onClick={buttonOnClick}
              sx={{
                color: theme.palette.background.default,
                px: 3,
                '&.Mui-disabled': {
                  backgroundColor: theme.palette.grey[800],
                  color: theme.palette.grey[200],
                },
              }}
            >
              {buttonLabel}
            </Button>

            {/* Next Button with icon */}
            <Button
              onClick={handleNext}
              variant="text"
              sx={{
                color: theme.palette.secondary.main,
                minWidth: 'auto',
                p: 1,
              }}
            >
              <ArrowForwardIosIcon fontSize="large" />
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Flyer Modal */}
      <Modal
        open={openFlyer}
        onClose={handleCloseFlyer}
        closeAfterTransition
        BackdropProps={{
          style: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          },
        }}
      >
        <Box
          onClick={handleCloseFlyer}
          sx={{
            height: '100%',
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Box
            component="img"
            src={currentTour.flyer}
            alt={`Flyer for ${currentTour.location}`}
            sx={{
              maxWidth: '98%',
              maxHeight: '98%',
              border: '4px solid white',
              objectFit: 'contain',
              cursor: 'pointer',
            }}
          />
        </Box>
      </Modal>

      {/* Payment Modal (imported) */}
      <PaymentModal
        open={openPaymentModal}
        onClose={handleClosePaymentModal}
        paymentTypes={currentTour.paymentTypes}
        doorDateTime={doorDateTime}
      />
    </Box>
  );
};

export default Tour;
